import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  createTown,
  CreateTownParams,
  deleteTown,
  readTowns,
  updateTown,
  UpdateTownParams,
} from 'src/api/plan';
import { Town } from 'src/api/types';
import { defaultServerError } from 'src/utils/consts';

import { messagesActions } from '../messages/slice';
import { sidesheetActions } from '../sidesheet/slice';
import { StoreState, ThunkConfig } from '../types';

import { getTownsState } from './selectors';

export const fetchTowns = createAsyncThunk<
  Map<number, Town>,
  boolean,
  ThunkConfig<string>
>(
  'towns/fetchTowns',
  async (cached, { getState, dispatch, rejectWithValue }) => {
    try {
      if (cached) {
        const state = getState() as StoreState;

        const { towns } = getTownsState(state);

        if (towns.size) return towns;
      }

      const response = await readTowns();
      const nextTowns: Map<number, Town> = new Map();
      for (const town of response) {
        nextTowns.set(town.townId, town);
      }

      return nextTowns;
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export const removeTown = createAsyncThunk<void, number, ThunkConfig<string>>(
  'towns/removeTown',
  async (id, { getState, dispatch, rejectWithValue }) => {
    try {
      const { towns } = getTownsState(getState() as StoreState);
      const town = towns.get(id);
      if (!town) return;

      await deleteTown(id);
      dispatch(
        messagesActions.show({
          message: `Город ${town.townName} успешно удален!`,
        }),
      );
      dispatch(sidesheetActions.close());
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);

export const addTown = createAsyncThunk<
  Town,
  CreateTownParams,
  ThunkConfig<string>
>('towns/addTown', async (params, { dispatch, rejectWithValue }) => {
  try {
    const town = await createTown(params);
    dispatch(
      messagesActions.show({
        message: `Город ${town.townName} успешно добавлен!`,
      }),
    );
    dispatch(sidesheetActions.close());

    return town;
  } catch (e) {
    dispatch(messagesActions.show({ message: defaultServerError }));
    return rejectWithValue((e as AxiosError).message);
  }
});

export const changeTown = createAsyncThunk<
  Town,
  UpdateTownParams,
  ThunkConfig<string>
>('towns/changeTown', async (params, { dispatch, rejectWithValue }) => {
  try {
    const town = await updateTown(params);
    dispatch(
      messagesActions.show({
        message: `Город ${town.townName} успешно обновлен!`,
      }),
    );
    dispatch(sidesheetActions.close());

    return town;
  } catch (e) {
    dispatch(messagesActions.show({ message: defaultServerError }));
    return rejectWithValue((e as AxiosError).message);
  }
});
